import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

export type {
  SalaryEmployeeListRow,
  SalaryEmployeeAllowance,
  SalaryEmployeeDetail,
  SalaryEmployeeAttachmentRow,
} from "src/types/salary";

export const salaryEmployeesKeys = {
  list: (tab: string, page: number, search: string) =>
    ["salary-employees", tab, page, search] as const,
  detail: (id: number) => ["salary-employee", id] as const,
  attachments: (id: number) => ["salary-employee-attachments", id] as const,
  outstandingLoans: (id: number) => ["salary-employee-loans", id] as const,
};

export interface IGetSalaryEmployeesParams {
  tab: "active" | "inactive";
  page?: number;
  search?: string;
}

export const useGetSalaryEmployees = (params: IGetSalaryEmployeesParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salaryEmployeesKeys.list(
      params.tab,
      params.page ?? 1,
      params.search ?? "",
    ),
    queryFn: async () => {
      return apiClient.get(`/api/employees`, {
        params: {
          tab: params.tab,
          page: params.page ?? 1,
          search: params.search?.trim() || undefined,
        },
      });
    },
  });

export const useGetSalaryEmployee = (id: number | null, enabled = true) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salaryEmployeesKeys.detail(id ?? 0),
    queryFn: async () => {
      return apiClient.get(`/api/employees/${id}`);
    },
    enabled: enabled && !!id && id > 0,
  });

export const useGetEmployeeOutstandingLoans = (
  employeeId: number | null,
  enabled = true,
) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salaryEmployeesKeys.outstandingLoans(employeeId ?? 0),
    queryFn: async () => {
      return apiClient.get(`/api/employees/${employeeId}/outstanding-loans`);
    },
    enabled: enabled && !!employeeId && employeeId > 0,
  });

export type SalaryEmployeePayload = {
  name: string;
  start_balance: number;
  basic_salary: number;
  description?: string | null;
  status?: boolean;
  allowances: { id?: number; name: string; amount: number }[];
};

export const useCreateSalaryEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, SalaryEmployeePayload>({
    mutationFn: (data) => apiClient.post(`/api/employees`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-employees"] });
    },
  });
};

export const useUpdateSalaryEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    APIErrorResponse,
    { id: number } & SalaryEmployeePayload
  >({
    mutationFn: ({ id, ...data }) =>
      apiClient.put(`/api/employees/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["salary-employees"] });
      queryClient.invalidateQueries({ queryKey: salaryEmployeesKeys.detail(variables.id) });
    },
  });
};

export const useToggleSalaryEmployeeActive = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, number>({
    mutationFn: (id) =>
      apiClient.patch(`/api/employees/${id}/toggleStatus`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-employees"] });
    },
  });
};

export const useDeleteSalaryEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation<any, APIErrorResponse, number>({
    mutationFn: (id) => apiClient.delete(`/api/employees/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-employees"] });
    },
  });
};

export const useGetSalaryEmployeeAttachments = (
  employeeId: number | null,
  enabled = false,
) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: salaryEmployeesKeys.attachments(employeeId ?? 0),
    queryFn: async () => {
      return apiClient.get(`/api/employeeFiles`, {
        params: { employee_id: employeeId },
      });
    },
    enabled: enabled && !!employeeId && employeeId > 0,
  });

export const useAddSalaryEmployeeAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    APIErrorResponse,
    { employeeId: number; file: File }
  >({
    mutationFn: ({ employeeId, file }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("employee_id", String(employeeId));
      return apiClient.post(`/api/employeeFiles`, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: salaryEmployeesKeys.attachments(variables.employeeId),
      });
      queryClient.invalidateQueries({ queryKey: salaryEmployeesKeys.detail(variables.employeeId) });
    },
  });
};

export const useDeleteSalaryEmployeeAttachment = () => {
  const queryClient = useQueryClient();
  return useMutation<
    any,
    APIErrorResponse,
    { employeeId: number; employeeFileId: number }
  >({
    mutationFn: ({ employeeFileId }) =>
      apiClient.delete(`/api/employeeFiles/${employeeFileId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: salaryEmployeesKeys.attachments(variables.employeeId),
      });
      queryClient.invalidateQueries({ queryKey: salaryEmployeesKeys.detail(variables.employeeId) });
    },
  });
};
